import type { AppThunk } from '../../../store';
import { pruneTenantSession, selectAllTenantSessions } from '.';
import { loadPerTenantTokens } from './tenantTokens';
import { switchActiveOrganization } from './switchActiveOrganization';

type TrySwitchFirstRemainingTenantSessionOptions = {
  /**
   * Caps loop iterations as a safety net. Sign-out uses
   * `MAX_SIGN_OUT_FALLBACK_ATTEMPTS`; unauthorized handling omits the cap and
   * relies on an empty `remaining` list (each iteration switches, prunes, or
   * stops when the registry is empty).
   */
  maxAttempts?: number;
};

/** Safety cap for sign-out fallback — avoids spinning on inconsistent Redux vs storage. */
export const MAX_SIGN_OUT_FALLBACK_ATTEMPTS = 10;

/**
 * After the active tenant was pruned, try switching to the first remaining
 * cached tenant (repeatedly until success, empty registry, or `maxAttempts`).
 */
export const trySwitchFirstRemainingTenantSession =
  (options: TrySwitchFirstRemainingTenantSessionOptions = {}): AppThunk<Promise<boolean>> =>
  async (dispatch, getState) => {
    const { maxAttempts } = options;
    let iteration = 0;

    while (true) {
      if (maxAttempts != null && iteration >= maxAttempts) {
        return false;
      }
      iteration += 1;

      const remaining = selectAllTenantSessions(getState());
      if (remaining.length === 0) {
        return false;
      }

      const candidate = remaining[0];

      if (!loadPerTenantTokens(candidate.tenantId)) {
        dispatch(pruneTenantSession({ tenantId: candidate.tenantId, alertUser: true }));
        continue;
      }

      const result = await dispatch(switchActiveOrganization(candidate.tenantId));
      if (result.ok) {
        return true;
      } else if (!result.hasPrunedSession) {
        dispatch(pruneTenantSession({ tenantId: candidate.tenantId, alertUser: true }));
      }
    }
  };
